import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Badge } from "@shared/ui/badge";
import { X } from "lucide-react";
import {
  useEntityTypes,
  useTerritoryJson,
  useConfigCode,
  usePostConfigMap,
  usePatchConfigMap,
  usePlanSearch,
  useTerritoryPremiseStats,
} from "../../../hooks";
import { useUtilityServices } from "@features/cx/consumerOnboarding/hooks";
import { useDebounce } from "@shared/hooks/useDebounce";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";
import { BUTTON_FIELD_CLASSES } from "@shared/utils/constants";
import { toast } from "@shared/ui/use-toast";

interface AddEntityModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: string;
  editMode?: boolean;
  editData?: any;
  onSubmit?: (data: any) => void;
}

const AddEntityModal: React.FC<AddEntityModalProps> = ({
  isOpen,
  onClose,
  entityType,
  editMode,
  editData,
  onSubmit,
}) => {
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");
  const [selectedEntityType, setSelectedEntityType] = useState("");
  const [entityName, setEntityName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCodeManuallyEdited, setIsCodeManuallyEdited] = useState(false);
  const utilityId = useRemoteUtilityId();
  const hasPopulatedEdit = useRef(false);

  const { startTracking, stopTracking } = useFormCompletionTracker(
    "Service Area Entity",
  );

  // Fetch entity types & utility services & plans
  const [selectedUtilityService, setSelectedUtilityService] = useState<
    string[] | undefined
  >();
  const { data: entityTypesData } = useEntityTypes();
  const { data: utilityData } = useUtilityServices();
  const { data: searchPlanData } = usePlanSearch({
    remoteUtilityId: utilityId,
    utilityService: selectedUtilityService,
  });

  // Derive entity code for edit mode (only present in edit mode, empty on add)
  const entityCode = useMemo(() => {
    if (!editMode || !editData) return "";
    const info = editData.basicInfo || editData.result?.basicInfo || editData;
    return info.code || info.configurationCode || editData.code || "";
  }, [editMode, editData]);

  // Hook to fetch stats data ONLY in edit mode (do NOT call on add)
  const { data: statsData } = useTerritoryPremiseStats(entityCode);

  const getUtilityOptions = useMemo(
    () =>
      utilityData
        ? utilityData
            .filter((utility: any) => utility.isActive === true)
            .map((utility: any) => ({
              value: utility.name,
              label: utility.name,
            }))
        : [],
    [utilityData],
  );

  const unitPlanOptions = useMemo(() => {
    const plans =
      searchPlanData?.result ||
      (Array.isArray(searchPlanData) ? searchPlanData : []);
    const optionsMap = new Map<string, { label: string; value: string }>();

    if (Array.isArray(plans) && plans.length > 0) {
      plans.forEach((p: any) => {
        const val = String(p.id);
        const name = p.planName || p.name || "";
        const short = p.shortName || "";
        optionsMap.set(val, {
          label: short ? `${name} · ${short}` : name,
          value: val,
        });
      });
    }

    // Always include current planInfo from statsData or editData if available
    const currentPlan = statsData?.planInfo || editData?.planInfo;

    if (currentPlan && (currentPlan.id || currentPlan.planId)) {
      const planVal = String(currentPlan.id || currentPlan.planId);
      if (!optionsMap.has(planVal)) {
        const name = currentPlan.planName || currentPlan.name || "N/A";
        const short = currentPlan.shortName || "";
        optionsMap.set(planVal, {
          label: short ? `${name} · ${short}` : name,
          value: planVal,
        });
      }
    }

    return Array.from(optionsMap.values());
  }, [searchPlanData, statsData, editData]);

  // Convert entity types to options (from API + hardcoded Unit until backend provides it)
  // Convert entity types to options from API (key: 2..11)
  const entityTypeOptions = useMemo(() => {
    return (entityTypesData?.result || []).map((type: any) => ({
      label: type.value,
      value: String(type.key),
    }));
  }, [entityTypesData]);

  // Map any input (slug string or numeric key) to exact numeric key string ("2" to "11")
  const getTypeKey = useCallback((input: string | number): string => {
    const str = String(input || "")
      .toLowerCase()
      .trim();
    if (!str) return "2";
    if (!isNaN(Number(str)) && Number(str) >= 2 && Number(str) <= 11) {
      return String(Number(str));
    }
    const slugMap: Record<string, string> = {
      region: "2",
      country: "3",
      state: "4",
      city: "5",
      county: "5",
      city_county: "5",
      "city/county": "5",
      zone: "6",
      division: "7",
      area: "8",
      areas: "8",
      sub_area: "9",
      sub_areas: "9",
      subarea: "9",
      premise: "10",
      premises: "10",
      unit: "11",
    };
    return slugMap[str] || "2";
  }, []);

  const debouncedEntityName = useDebounce(editMode ? "" : entityName, 300);

  // Post mutation hook
  const postConfigMapMutation = usePostConfigMap();
  const patchMutation = usePatchConfigMap();

  // Get parent config level string for useTerritoryJson based on selected entity type key
  const getParentConfigLevel = useCallback(
    (typeInput: string | number) => {
      const key = getTypeKey(typeInput);
      const parentMap: Record<string, string> = {
        "11": "premise",
        "10": "sub_area",
        "9": "area",
        "8": "division",
        "7": "zone",
        "6": "county",
        "5": "state",
        "4": "country",
        "3": "region",
      };
      return parentMap[key];
    },
    [getTypeKey],
  );

  const parentConfigLevel = getParentConfigLevel(selectedEntityType);
  const { data: parentOptionsData } = useTerritoryJson(parentConfigLevel || "");

  // Fetch config code when entity name changes
  const { data: configCodeData } = useConfigCode(
    editMode ? "" : debouncedEntityName,
  );

  // Convert parent options
  const parentOptions = useMemo(() => {
    if (parentOptionsData?.result && parentOptionsData.result.length > 0) {
      return parentOptionsData.result.map((item) => ({
        label: item.name,
        value: item.code,
      }));
    }
    return [];
  }, [parentOptionsData]);

  // Get entity title
  const getEntityTitle = useCallback(
    (typeInput: string | number) => {
      const key = getTypeKey(typeInput);
      const option = entityTypeOptions.find(
        (o: any) => String(o.value) === key,
      );
      return option?.label || "Entity";
    },
    [entityTypeOptions, getTypeKey],
  );

  const getFieldDescription = useCallback(
    (field: string, typeInput: string | number) => {
      const key = getTypeKey(typeInput);
      const descriptions: Record<string, Record<string, string>> = {
        name: {
          "2": "Enter a unique name for the region (e.g., North Region, West Coast)",
          "3": "Official country name as per government records",
          "4": "State or province name within the selected country",
          "5": "City, county, or municipal area name",
          "6": "Service zone identifier within the city/county",
          "7": "Operational division name for management purposes",
          "8": "Service area designation for field operations",
          "9": "Specific sub-area within the parent area",
          "10": "Building or property identifier (address, name, or code)",
          "11": "Building or property identifier (address, name, or code)",
        },
        code: {
          "2": "Auto-generated unique code based on region name",
          "3": "Auto-generated unique code based on country name",
          "4": "Auto-generated unique code based on state name",
          "5": "Auto-generated unique code based on city name",
          "6": "Auto-generated unique code based on zone name",
          "7": "Auto-generated unique code based on division name",
          "8": "Auto-generated unique code based on area name",
          "9": "Auto-generated unique code based on sub-area name",
          "10": "Auto-generated unique code based on premise name",
          "11": "Auto-generated unique code based on unit name (can be edited)",
        },
        parent: {
          "3": "Select the region this country belongs to for organizational hierarchy",
          "4": "Choose the country this state/province is located in",
          "5": "Select the state/province this city is part of",
          "6": "Choose the city/county this zone serves",
          "7": "Select the zone this division operates under",
          "8": "Choose the division this area is assigned to",
          "9": "Select the parent area this sub-area is part of",
          "10": "Choose the sub-area where this premise is located",
          "11": "Choose the premises where this unit is located",
        },
        description: {
          "2": "Describe the geographical coverage and key characteristics of this region",
          "3": "Add details about service coverage, regulations, or special notes",
          "4": "Include population served, key cities, or operational notes",
          "5": "Describe urban/rural classification, population, and service characteristics",
          "6": "Define service boundaries, customer types, and operational details",
          "7": "Specify management responsibilities and service scope",
          "8": "Detail field operation boundaries and service delivery areas",
          "9": "Describe specific locality characteristics and service requirements",
          "10": "Add building details, access information, and special requirements",
          "11": "Add building details, access information, and special requirements",
        },
      };
      return (
        descriptions[field]?.[key] || "Provide relevant details for this field"
      );
    },
    [getTypeKey],
  );

  // Generate form fields dynamically
  const generateFormFields = useCallback((): FormField[] => {
    const currentKey = getTypeKey(selectedEntityType);
    const isUnit = currentKey === "11";
    const isRegion = currentKey === "2";
    const currentEntityTitle = getEntityTitle(selectedEntityType);

    const fields: FormField[] = [
      {
        name: "entityType",
        type: "select",
        label: "Entity Type",
        required: true,
        disabled: editMode,
        placeholder: "Select entity type",
        defaultValue: selectedEntityType,
        options: entityTypeOptions,
        helperText:
          "Choose the type of service entity you want to create in the hierarchy",
        className: "col-span-2",
      },
      {
        name: "name",
        type: "text",
        label: isUnit ? "Unit Name" : `${currentEntityTitle} Name`,
        required: true,
        disabled: editMode,
        placeholder: isUnit
          ? "Enter unit name"
          : `Enter ${currentEntityTitle.toLowerCase()} name`,
        helperText: isUnit
          ? "Building or property identifier (address, name, or code)"
          : getFieldDescription("name", selectedEntityType),
        className: "col-span-1",
      },
      {
        name: "code",
        type: "text",
        label: isUnit ? "Unit Code" : `${currentEntityTitle} Code`,
        required: true,
        placeholder: isUnit
          ? "Auto-generated unit code"
          : `Auto-generated ${currentEntityTitle.toLowerCase()} code`,
        helperText: isUnit
          ? "Auto-generated unique code based on unit name (can be edited)"
          : "This code is automatically generated based on the entity name",
        disabled: editMode,
        className: "col-span-1",
      },
      {
        name: "parent",
        type: "select",
        label: isUnit ? "Parent Entity (Premises)" : "Parent Entity",
        required: !isRegion,
        placeholder: "Select parent entity",
        options: parentOptions,
        helperText: isUnit
          ? "Choose the premises where this unit is located"
          : getFieldDescription("parent", selectedEntityType),
        disabled: editMode,
        showWhen: (formData: any) => {
          const key = getTypeKey(formData.entityType || selectedEntityType);
          return key !== "2" && key !== "";
        },
        className: "col-span-2",
      },
      {
        name: "unitType",
        type: "select",
        label: "Unit Type",
        required: true,
        placeholder: "Select property type",
        options: [
          { label: "Residential", value: "Residential" },
          { label: "Commercial", value: "Commercial" },
          { label: "Industrial", value: "Industrial" },
          { label: "Mixed-Use", value: "Mixed-Use" },
          { label: "Agricultural", value: "Agricultural" },
          { label: "Government / Municipal", value: "Government / Municipal" },
          { label: "Educational", value: "Educational" },
          { label: "Healthcare", value: "Healthcare" },
        ],
        helperText: "Select the classification that best describes this unit",
        showWhen: (formData: any) =>
          getTypeKey(formData.entityType || selectedEntityType) === "11",
        className: "col-span-2",
      },
      {
        name: "locationType",
        type: "select",
        label: "Location Type",
        placeholder: "Select location type",
        helperText: "Urban classification of the property's location",
        options: [
          { label: "Urban", value: "Urban" },
          { label: "Suburban", value: "Suburban" },
          { label: "Semi-Urban", value: "Semi-Urban" },
          { label: "Rural", value: "Rural" },
          { label: "Suburb", value: "Suburb" },
          { label: "Metropolitan", value: "Metropolitan" },
        ],
        showWhen: (formData: any) =>
          getTypeKey(formData.entityType || selectedEntityType) === "11",
        className: "col-span-1",
      },
      {
        name: "occupancyStatus",
        type: "select",
        label: "Occupancy Status",
        placeholder: "Select status",
        helperText: "Current occupancy state of the property",
        options: [
          { label: "Occupied", value: "Occupied" },
          { label: "Vacant", value: "Vacant" },
          { label: "Under Construction", value: "Under Construction" },
          { label: "Under Renovation", value: "Under Renovation" },
          { label: "Partially Occupied", value: "Partially Occupied" },
        ],
        showWhen: (formData: any) =>
          getTypeKey(formData.entityType || selectedEntityType) === "11",
        className: "col-span-1",
      },
      {
        name: "ownershipType",
        type: "select",
        label: "Ownership Type",
        placeholder: "Select ownership type",
        helperText: "Legal ownership classification of this property",
        options: [
          { label: "Owned", value: "Owned" },
          { label: "Leased", value: "Leased" },
          { label: "Government", value: "Government" },
          { label: "Co-operative", value: "Co-operative" },
          { label: "Mixed", value: "Mixed" },
        ],
        showWhen: (formData: any) =>
          getTypeKey(formData.entityType || selectedEntityType) === "11",
        className: "col-span-2",
      },
      {
        name: "utilityServices",
        type: "multi-select",
        label: "Utility Services",
        required: false,
        placeholder: "Select utility services",
        helperText: "Select all utility services available for this property",
        options: getUtilityOptions,
        showWhen: (formData: any) =>
          getTypeKey(formData.entityType || selectedEntityType) === "11",
        className: "col-span-2",
      },
      {
        name: "unitPlan",
        type: "select",
        label: "Select Plan",
        required: false,
        showHelperTooltip: true,
        tooltipText: "Attach a predefined plan to this unit",
        placeholder: "— Select a plan —",
        helperText: "Attach a predefined plan to this unit",
        options: unitPlanOptions,
        showWhen: (formData: any) =>
          getTypeKey(formData.entityType || selectedEntityType) === "11",
        className: "col-span-2",
      },
      {
        name: "unitPlanDetailCard",
        type: "custom",
        showWhen: (formData: any) =>
          getTypeKey(formData.entityType || selectedEntityType) === "11" &&
          !!formData.unitPlan,
        className: "col-span-2",
        customRender: (field, form) => {
          const selectedPlanVal = form.watch("unitPlan") || "";
          const plansList =
            searchPlanData?.result ||
            (Array.isArray(searchPlanData) ? searchPlanData : []);
          const selectedPlanObj = plansList.find(
            (p: any) =>
              String(p.id) === String(selectedPlanVal) ||
              String(p.shortName) === String(selectedPlanVal) ||
              String(p.planName) === String(selectedPlanVal),
          );
          if (!selectedPlanObj) return null;

          const utilityServicesList = Array.isArray(
            selectedPlanObj.utilityService,
          )
            ? selectedPlanObj.utilityService
            : selectedPlanObj.utilityService
              ? [selectedPlanObj.utilityService]
              : [];

          return (
            <div className="p-4 border border-gray-200 bg-[#f8fafc] rounded-xl space-y-3 my-1 col-span-2">
              <div className="flex items-center justify-between">
                <span className="font-bold text-[#1e293b] text-sm sm:text-base">
                  {selectedPlanObj.planName}
                </span>
                {selectedPlanObj.shortName && (
                  <span className="font-mono text-xs text-gray-600 bg-white border border-gray-200 px-3 py-1 rounded-md shadow-2xs font-medium">
                    {selectedPlanObj.shortName}
                  </span>
                )}
              </div>
              {utilityServicesList.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-0.5">
                  {utilityServicesList.map((serviceName: string) => (
                    <span
                      key={serviceName}
                      className="text-xs font-semibold px-3 py-1 rounded-md border bg-[#eff6ff] border-[#bfdbfe] text-[#2563eb]"
                    >
                      {serviceName}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        },
      },
      {
        name: "latitude",
        type: "number",
        label: "Latitude",
        placeholder: "e.g. 40.712800",
        helperText: "Decimal degrees, e.g. 40.712800 (–90 to 90)",
        customValidation: (val: any) => {
          if (val === "" || val === null || val === undefined) return true;
          const num = Number(val);
          if (isNaN(num)) return "Must be a valid number";
          if (num < -90 || num > 90) return "Must be between –90 and 90";
          return true;
        },
        showWhen: (formData: any) =>
          getTypeKey(formData.entityType || selectedEntityType) === "11",
        className: "col-span-1",
      },
      {
        name: "longitude",
        type: "number",
        label: "Longitude",
        placeholder: "e.g. -74.005974",
        helperText: "Decimal degrees, e.g. –74.005974 (–180 to 180)",
        customValidation: (val: any) => {
          if (val === "" || val === null || val === undefined) return true;
          const num = Number(val);
          if (isNaN(num)) return "Must be a valid number";
          if (num < -180 || num > 180) return "Must be between –180 and 180";
          return true;
        },
        showWhen: (formData: any) =>
          getTypeKey(formData.entityType || selectedEntityType) === "11",
        className: "col-span-1",
      },
      {
        name: "parcelId",
        type: "text",
        label: "Parcel ID",
        required: false,
        placeholder: "Enter parcel ID (e.g., PRC-00001)",
        helperText:
          "Unique parcel identification number assigned to this premise",
        showWhen: (formData: any) =>
          getTypeKey(formData.entityType || selectedEntityType) === "10",
        classes: {
          container: "w-full mb-4 col-span-1",
          label: "block text-sm font-medium text-gray-700 mb-1",
          input:
            "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
      },
      {
        name: "totalUnits",
        type: "number",
        label: "Total Units (Capacity)",
        required: true,
        defaultValue: "",
        min: 0,
        placeholder: "e.g. 49",
        helperText: "Maximum number of units this premise can hold",
        customValidation: (val: any) => {
          if (val === "" || val === null || val === undefined) return true;
          const num = Number(val);
          if (isNaN(num)) return "Must be a valid number";
          if (num < 0) return "Total units cannot be negative";
          return true;
        },
        showWhen: (formData: any) =>
          getTypeKey(formData.entityType || selectedEntityType) === "10",
        classes: {
          container: "w-full mb-4 col-span-1",
          label: "block text-sm font-medium text-gray-700 mb-1",
          input:
            "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
      },
      {
        name: "description",
        type: "textarea",
        label: "Description",
        required: false,
        showHelperTooltip: true,
        tooltipText:
          "Add building details, access information, and special requirements",
        placeholder: isUnit
          ? "Enter unit description"
          : `Enter ${currentEntityTitle.toLowerCase()} description`,
        helperText: getFieldDescription("description", selectedEntityType),
        classes: {
          container: "w-full mb-4 col-span-2",
          label: "block text-sm font-medium text-gray-700 mb-1",
          textarea:
            "w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white resize-none",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
      },
    ];

    return fields;
  }, [
    selectedEntityType,
    editMode,
    entityTypeOptions,
    parentOptions,
    unitPlanOptions,
    getUtilityOptions,
    searchPlanData,
    getEntityTitle,
    getFieldDescription,
    getTypeKey,
  ]);

  const extractBasicInfo = useCallback(
    (data: any) => {
      if (!data) return {};
      const basicInfo = data.basicInfo || data.result?.basicInfo || data;
      const parentInfo = data.parentInfo || data.result?.parentInfo || {};
      const planInfo = data.planInfo || data.result?.planInfo || {};

      const parentCode =
        parentInfo.parentCode || basicInfo.parentCode || basicInfo.parent || "";

      return {
        entityType:
          basicInfo.entityType ||
          basicInfo.entityTypeKey ||
          basicInfo.entityTypeDisplay ||
          (basicInfo.unitType ? "11" : entityType || ""),
        name: basicInfo.name || "",
        code: basicInfo.configurationCode || basicInfo.code || "",
        parent: parentCode || "",
        unitType: basicInfo.unitType || "",
        locationType: basicInfo.locationType || "",
        occupancyStatus: basicInfo.occupancyStatus || "",
        ownershipType: basicInfo.ownershipType || "",
        utilityServices: basicInfo.utilityServices || undefined,
        unitPlan:
          basicInfo.unitPlan !== undefined &&
          basicInfo.unitPlan !== null &&
          basicInfo.unitPlan !== ""
            ? String(basicInfo.unitPlan)
            : basicInfo.planId !== undefined &&
                basicInfo.planId !== null &&
                basicInfo.planId !== ""
              ? String(basicInfo.planId)
              : basicInfo.plan_id !== undefined &&
                  basicInfo.plan_id !== null &&
                  basicInfo.plan_id !== ""
                ? String(basicInfo.plan_id)
                : planInfo.id || planInfo.plan_id
                  ? String(planInfo.id || planInfo.plan_id)
                  : "",
        parcelId: parentInfo.parcelId || basicInfo.parcelId || "",
        totalUnits:
          parentInfo.totalUnits !== undefined &&
          parentInfo.totalUnits !== null &&
          parentInfo.totalUnits !== ""
            ? String(parentInfo.totalUnits)
            : basicInfo.totalUnits !== undefined &&
                basicInfo.totalUnits !== null &&
                basicInfo.totalUnits !== ""
              ? String(basicInfo.totalUnits)
              : "",
        latitude: basicInfo.latitude ?? "",
        longitude: basicInfo.longitude ?? "",
        description: basicInfo.description || "",
        status: basicInfo.isActive === false ? "inactive" : "active",
        tags:
          basicInfo.tags && Array.isArray(basicInfo.tags) ? basicInfo.tags : [],
      };
    },
    [entityType],
  );

  const defaultValues = useMemo(() => {
    if (editMode && editData) {
      const info = extractBasicInfo(editData);
      return {
        entityType: selectedEntityType || getTypeKey(info.entityType),
        name: info.name,
        code: info.code,
        parent: info.parent,
        unitType: info.unitType,
        locationType: info.locationType,
        occupancyStatus: info.occupancyStatus,
        ownershipType: info.ownershipType,
        utilityServices: info.utilityServices,
        unitPlan: info.unitPlan,
        parcelId: info.parcelId,
        totalUnits: info.totalUnits,
        latitude: info.latitude,
        longitude: info.longitude,
        description: info.description,
        status: info.status,
      };
    }
    return {
      entityType: selectedEntityType,
      name: "",
      code: "",
      parent: "",
      unitType: "Residential",
      locationType: "Urban",
      occupancyStatus: "Occupied",
      ownershipType: "Owned",
      utilityServices: undefined,
      unitPlan: "",
      parcelId: "",
      totalUnits: "",
      latitude: "",
      longitude: "",
      description: "",
      status: "active",
    };
  }, [selectedEntityType, editData, editMode, extractBasicInfo, getTypeKey]);

  // Create form instance
  const form = FormService.createForm({
    fields: generateFormFields(),
    defaultValues,
    mode: "onBlur",
    validateOnMount: false,
  });

  useEffect(() => {
    if (isOpen) {
      startTracking();
      if (!editMode && entityType) {
        const typeKey = getTypeKey(entityType);
        setSelectedEntityType(typeKey);
        form.setValue("entityType", typeKey);
        form.setValue("totalUnits", "");
        form.setValue("parcelId", "");
      }
    }
  }, [isOpen, entityType, editMode, startTracking, form, getTypeKey]);

  useEffect(() => {
    if (!isOpen) {
      hasPopulatedEdit.current = false;
      return;
    }

    const dataToPopulate = editMode && statsData ? statsData : editData;

    if (editMode && dataToPopulate) {
      const info = extractBasicInfo(dataToPopulate);
      const normalizedKey = getTypeKey(info.entityType);

      setSelectedEntityType(normalizedKey);
      setEntityName(info.name);

      form.setValue("entityType", normalizedKey);
      form.setValue("name", info.name);
      form.setValue("code", info.code);
      if (info.parent) {
        form.setValue("parent", info.parent);
      }
      form.setValue("unitType", info.unitType);
      form.setValue("locationType", info.locationType);
      form.setValue("occupancyStatus", info.occupancyStatus);
      form.setValue("ownershipType", info.ownershipType);

      if (info.utilityServices) {
        const arr = Array.isArray(info.utilityServices)
          ? info.utilityServices
          : [info.utilityServices];
        form.setValue("utilityServices", arr);
        setSelectedUtilityService(arr);
      }

      if (info.unitPlan) {
        form.setValue("unitPlan", info.unitPlan);
      }

      if (info.parcelId) {
        form.setValue("parcelId", info.parcelId);
      }

      if (info.totalUnits) {
        form.setValue("totalUnits", info.totalUnits);
      }

      if (
        info.latitude !== "" &&
        info.latitude !== null &&
        info.latitude !== undefined
      ) {
        form.setValue("latitude", info.latitude);
      }

      if (
        info.longitude !== "" &&
        info.longitude !== null &&
        info.longitude !== undefined
      ) {
        form.setValue("longitude", info.longitude);
      }

      form.setValue("description", info.description);
      form.setValue("status", info.status);

      if (info.tags && Array.isArray(info.tags)) {
        setTags(info.tags);
      }

      if (statsData) {
        hasPopulatedEdit.current = true;
      }
    }
  }, [
    isOpen,
    editMode,
    editData,
    statsData,
    form,
    getTypeKey,
    extractBasicInfo,
  ]);

  useEffect(() => {
    if (!editMode && !isCodeManuallyEdited) {
      if (entityName.trim().length > 0 && configCodeData) {
        const generatedCode =
          configCodeData?.configurationCode ||
          configCodeData?.result?.configurationCode ||
          (typeof configCodeData === "string" ? configCodeData : "");
        if (typeof generatedCode === "string" && generatedCode.trim()) {
          form.setValue("code", generatedCode);
        }
      } else if (entityName.trim().length === 0) {
        form.setValue("code", "");
      }
    }
  }, [configCodeData, form, entityName, editMode, isCodeManuallyEdited]);

  const handleAddTag = () => {
    const trimmed = newTag.trim();
    if (!trimmed) return;
    if (tags.includes(trimmed)) {
      toast({
        title: "Duplicate Tag",
        description: "This tag has already been added.",
        variant: "destructive",
      });
      return;
    }
    setTags([...tags, trimmed]);
    setNewTag("");
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      if (fieldName === "name" && !editMode) {
        setEntityName(value || "");
        setIsCodeManuallyEdited(false);
      }

      if (fieldName === "code" && !editMode) {
        const generatedCode =
          configCodeData?.configurationCode ||
          configCodeData?.result?.configurationCode ||
          (typeof configCodeData === "string" ? configCodeData : "");
        if (value !== generatedCode) {
          setIsCodeManuallyEdited(true);
        }
      }

      if (fieldName === "utilityServices") {
        const services = Array.isArray(value)
          ? value
          : typeof value === "string" && value.trim()
            ? [value]
            : undefined;
        setSelectedUtilityService(
          services && services.length > 0 ? services : undefined,
        );
      }

      if (fieldName === "entityType" && !editMode) {
        const newEntityType = String(value || "");
        setSelectedEntityType(newEntityType);
        setIsCodeManuallyEdited(false);
        setTimeout(() => {
          form.setValue("parent", "");
          form.setValue("code", "");
          form.clearErrors();
          setEntityName("");
        }, 0);
      }

      if (FormService.getFieldError(form, fieldName)) {
        form.clearErrors(fieldName);
      }
    },
    [form, editMode, configCodeData],
  );

  const resetForm = useCallback(() => {
    form.reset();
    setTags([]);
    setNewTag("");
    setEntityName("");
    setIsCodeManuallyEdited(false);
    setSelectedEntityType(editMode ? "" : "");
    setIsSubmitting(false);
    hasPopulatedEdit.current = false;
  }, [form, entityType, editMode]);

  const buildPayload = (formData: any) => {
    const rawType = formData.entityType || selectedEntityType;
    const key = getTypeKey(rawType);

    const payload: any = {
      mapType: "TERRITORY_JSON",
      remote_utility_id: utilityId,
      entity_type: Number(key),
      name: formData.name,
      configuration_code: formData.code,
      description: formData.description || "",
      tags,
    };

    const isActive = formData.status ? formData.status === "active" : true;
    payload.is_active = isActive;

    if (key === "10") {
      if (formData.parcelId) {
        payload.parcel_id = formData.parcelId;
      }
      if (formData.totalUnits) {
        payload.total_units = String(formData.totalUnits);
      }
    }

    if (key === "11") {
      payload.unit_type = formData.unitType;
      payload.location_type = formData.locationType;
      payload.occupancy_status = formData.occupancyStatus;
      payload.ownership_type = formData.ownershipType;
      payload.utility_services = formData.utilityServices;
      payload.plan_id = formData.unitPlan ? Number(formData.unitPlan) : null;
      payload.latitude = formData.latitude;
      payload.longitude = formData.longitude;
      if (formData.totalUnits) {
        payload.total_units = String(formData.totalUnits);
      }
    }

    if (editMode) {
      payload.code =
        editData?.code ||
        editData?.basicInfo?.code ||
        statsData?.basicInfo?.code ||
        formData.code;
    } else if (key === "2") {
      payload.code = formData.name;
    } else if (formData.parent) {
      payload.code = `${formData.parent}_${formData.name}`;
    }

    return payload;
  };

  // Handle form submission
  const handleSubmit = async (formData: any) => {
    try {
      setIsSubmitting(true);
      const payload = buildPayload(formData);
      const currentEntityTitle = getEntityTitle(selectedEntityType);

      if (editMode) {
        await patchMutation.mutateAsync(payload);
        toast({
          title: "Success",
          description: `${currentEntityTitle} updated successfully`,
        });
        if (onSubmit) {
          onSubmit(payload);
        }
      } else {
        await postConfigMapMutation.mutateAsync(payload);
        stopTracking();
        toast({
          title: "Success",
          description: `${currentEntityTitle} created successfully`,
        });

        if (onSubmit) {
          onSubmit(payload);
        }
      }

      resetForm();
      onClose();
    } catch (error: any) {
      console.error("Form submission error:", error);
      const apiErrorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        "An error occurred while submitting. Please check your entries and try again.";

      // Map backend field-level validation errors to form fields
      const fieldErrors = error?.response?.data?.errors;
      if (fieldErrors && typeof fieldErrors === "object") {
        Object.keys(fieldErrors).forEach((fieldKey) => {
          const fieldMessage = Array.isArray(fieldErrors[fieldKey])
            ? fieldErrors[fieldKey][0]
            : fieldErrors[fieldKey];
          if (fieldMessage) {
            form.setError(fieldKey as any, {
              type: "manual",
              message: String(fieldMessage),
            });
          }
        });
      }

      toast({
        title: editMode ? "Update Failed" : "Creation Failed",
        description: String(apiErrorMessage),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  // Custom footer with tags and actions
  const renderCustomFooter = useCallback(
    () => (
      <div className="space-y-4">
        {/* Tags Section */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Tags
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              placeholder="Add tag"
              className="flex-1 h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white"
              onKeyPress={(e) =>
                e.key === "Enter" && (e.preventDefault(), handleAddTag())
              }
              disabled={isSubmitting}
            />
            <Button
              type="button"
              onClick={handleAddTag}
              variant="outline"
              disabled={isSubmitting}
            >
              Add
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Add tags for easy searching and categorization (e.g., high-priority,
            rural, industrial)
          </p>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="cursor-pointer">
                  {tag}
                  <X
                    className="w-3 h-3 ml-1"
                    onClick={() => !isSubmitting && removeTag(tag)}
                  />
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? editMode
                ? "Updating..."
                : "Creating..."
              : editMode
                ? `Update ${getEntityTitle(selectedEntityType)}`
                : `Create ${getEntityTitle(selectedEntityType)}`}
          </Button>
        </div>
      </div>
    ),
    [tags, newTag, selectedEntityType, handleClose, isSubmitting, editMode],
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {editMode
              ? `Edit ${getEntityTitle(selectedEntityType)}`
              : `Add New ${getEntityTitle(selectedEntityType)}`}
          </DialogTitle>
        </DialogHeader>

        <DynamicForm
          fields={generateFormFields()}
          form={form}
          onSubmit={handleSubmit}
          onReset={resetForm}
          onFieldChange={handleFieldChange}
          gridColumns={2}
          responsiveColumns={{
            sm: 1,
            md: 2,
          }}
          config={{
            showProgress: false,
            layout: "vertical",
            spacing: "comfortable",
            submitButtonText: isSubmitting
              ? editMode
                ? "Updating..."
                : "Creating..."
              : editMode
                ? `Update ${getEntityTitle(selectedEntityType)}`
                : `Create ${getEntityTitle(selectedEntityType)}`,
            showResetButton: false,
            resetOnSubmit: false,
            classes: BUTTON_FIELD_CLASSES,
            defaultValues: defaultValues,
          }}
          renderCustomFooter={renderCustomFooter}
        />
      </DialogContent>
    </Dialog>
  );
};

export default AddEntityModal;
